import React, { useState } from 'react';
import { ButtonCallToAction, ButtonTertiary, PromptConfirmation } from 'ui';
import { StickyHeader, StickyHeaderButtonContainer } from 'app/shell/components/sticky-header/sticky-header';
import { UserMembersTranslation } from '../constants';
import { useActionCreators, useListActionsCallbacks, useListCallbacks, useListSelectors } from '../hooks';
import { UserMembersListHeaderActions } from './user-members-list-header-actions';
import { UserMembersListHeaderVisibility } from './user-members-list-header-visibility';
import { Visibility } from 'app/area/subscriber-groups/user-members/types-store';
import { searchFilterChanged } from 'app/area/subscriber-groups/user-members/utils/search-filter-changed';
import { toastFactory } from 'app/infrastructure/notifications/toast-factory';
import styles from './user-members-list-header.scss';

interface UserMembersListHeaderProps {
  subscriberGroupId: number;
  hasChanges: boolean;
  displayNumberOfResults: boolean;
  numberOfResults: number;
  translation: UserMembersTranslation;
}

export const UserMembersListHeader: React.FC<UserMembersListHeaderProps> = ({
  hasChanges, displayNumberOfResults, numberOfResults, translation
}) => {

  const toasts = toastFactory();
  const { requestedModel, visiblity, subscriberGroupValue } = useListSelectors();
  const [shouldPrompt, setShouldPrompt] = useState(false);
  const { updateVisibility } = useListCallbacks();
  const { loadListBranch, clearList } = useActionCreators();
  const showPrompt = () => {
    setShouldPrompt(true);
  };

  const hidePrompt = () => {
    setShouldPrompt(false);
  };

  const { saveChanges, cancelChanges } = useListActionsCallbacks();

  const onSave = () => {
    saveChanges(translation.message_user_members_updated, translation.validation_error);
  };

  const showNotification = (nextVisibility: Visibility) => {
    if (visiblity !== nextVisibility) {
      toasts.dispatchInfo(translation.message_limit_visibility);
    }
  };

  const setVisibility = (newVisibility: Visibility) =>
    hasChanges
      ? showNotification(newVisibility)
      : applyVisibility(newVisibility);

  const showAll = () => setVisibility(Visibility.all);
  const showSelected = () => setVisibility(Visibility.selected);
  const showUnSelected = () => setVisibility(Visibility.unselected);

  const applyVisibility = (newVisibility: Visibility) => {
    const visibilityActions = new Map<Visibility, () => void>([
      [Visibility.selected, onShowSelected],
      [Visibility.unselected, onShowUnselected],
      [Visibility.all, onShowAll]
    ]);
    visibilityActions.get(newVisibility)();
  };

  const onShowAll = () => {
    loadList(true);
    updateVisibility(Visibility.all);
  };
  const onShowSelected = () => {
    loadList(false, false);
    updateVisibility(Visibility.selected);
  };
  const onShowUnselected = () => {
    loadList(false, true);
    updateVisibility(Visibility.unselected);
  };

  const loadList = (all?: boolean, negated?: boolean) => {
    const model = {
      ...requestedModel,
      subscriberGroup: subscriberGroupValue?.value,
      showAll: all,
      ...(!all && { negated }),
      ...(!!all && { showAll: all })
    };

    clearList(model);
    loadListBranch(model);
  };

  return <div className="user-members-list-header">
    {shouldPrompt && <PromptConfirmation
      title={translation.cancel_changes}
      description={translation.are_you_sure}
      continueText={translation.undo_all}
      cancelText={translation.go_back}
      onContinue={cancelChanges}
      onCancel={hidePrompt} />}
    <StickyHeader
      className={styles.header}
      rightSideContent={<StickyHeaderButtonContainer>
        <div className={`${styles.buttonContainer} user-members-list-header-buttons`}>
          <ButtonTertiary
            id="user_members_cancel_changes"
            onClick={showPrompt}
            disabled={!hasChanges}>
            {translation.cancel_changes}
          </ButtonTertiary>
          <ButtonCallToAction
            id="texts_save_changes"
            onClick={onSave}
            disabled={!hasChanges}>
            {translation.update_codes}
          </ButtonCallToAction>
        </div>
      </StickyHeaderButtonContainer>}>
      <div className={styles.dropdownContainer}>
        <UserMembersListHeaderActions
          searchFilterChanged={searchFilterChanged(requestedModel)}
          translation={translation} />
        <UserMembersListHeaderVisibility
          visiblity={visiblity}
          translation={translation}
          showAll={showAll}
          showSelected={showSelected}
          showUnSelected={showUnSelected} />
      </div>
    </StickyHeader>

    <div className={styles.searchSummary}>
      <div id="user_members_number_of_results" className={styles.numberOfResults}>
        {displayNumberOfResults && numberOfResults
          ? `${numberOfResults} ${translation.search_hits}`
          : null}
      </div>
    </div>
  </div>;
};
